/**
 * GameRtpAdminTab
 * Admin → Settings → Game RTP.
 * Backed by /api/admin/game-rtp.
 * Operators can dial each server-authoritative game's Return-To-Player (RTP)
 * target globally AND per operator/location. Lower RTP = higher house edge =
 * more operator profit. Also shows a live "actual RTP over last N spins"
 * readout and a change log of who adjusted what and when.
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { RefreshCw, Percent, Save, Trash2, Plus, History, Activity, Flame } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const pct = (n) => (n == null ? '—' : `${Number(n).toFixed(2)}%`);
const when = (t) => {
    if (!t) return '';
    try { return new Date(t).toLocaleString(); } catch { return String(t); }
};

export default function GameRtpAdminTab() {
    const [games, setGames] = useState([]);
    const [operators, setOperators] = useState([]);
    const [drafts, setDrafts] = useState({});        // game_id -> global target string
    const [live, setLive] = useState({});            // game_id -> live payload
    const [logs, setLogs] = useState({});            // game_id -> [rows]
    const [openLog, setOpenLog] = useState({});      // game_id -> bool
    const [ovForm, setOvForm] = useState({});        // game_id -> {operator_id, target}
    const [loading, setLoading] = useState(false);
    const [savingId, setSavingId] = useState(null);
    const [bonusFreq, setBonusFreq] = useState(null);   // lucky-ducky bonus-frequency dial
    const [savingFreq, setSavingFreq] = useState(false);

    const loadBonusFreq = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/admin/lucky-ducky/bonus-frequency`, { withCredentials: true });
            setBonusFreq(data);
        } catch { /* ignore */ }
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [{ data }, ops] = await Promise.all([
                axios.get(`${API}/admin/game-rtp`, { withCredentials: true }),
                axios.get(`${API}/admin/operators`, { withCredentials: true }).catch(() => ({ data: [] })),
            ]);
            setGames(data.games || []);
            setOperators(ops.data || []);
            const d = {};
            (data.games || []).forEach(g => { d[g.game_id] = String(g.target_rtp); });
            setDrafts(d);
            (data.games || []).forEach(g => loadLive(g.game_id));
            loadBonusFreq();
        } catch (e) {
            toast.error('Failed to load game RTP settings');
        } finally {
            setLoading(false);
        }
    }, []);

    const loadLive = useCallback(async (gameId) => {
        try {
            const { data } = await axios.get(`${API}/admin/game-rtp/${gameId}/live?n=5000`, { withCredentials: true });
            setLive(prev => ({ ...prev, [gameId]: data }));
        } catch { /* ignore */ }
    }, []);

    const loadLog = useCallback(async (gameId) => {
        try {
            const { data } = await axios.get(`${API}/admin/game-rtp/${gameId}/log?limit=25`, { withCredentials: true });
            setLogs(prev => ({ ...prev, [gameId]: data.log || [] }));
        } catch { /* ignore */ }
    }, []);

    useEffect(() => { load(); }, [load]);

    const saveGlobal = async (g) => {
        const target = parseFloat(drafts[g.game_id]);
        if (isNaN(target) || target < 30 || target > g.baseline_rtp) {
            toast.error(`Target must be between 30% and the baseline ${g.baseline_rtp}%`);
            return;
        }
        setSavingId(`${g.game_id}-global`);
        try {
            await axios.put(`${API}/admin/game-rtp/${g.game_id}`, { target_rtp: target }, { withCredentials: true });
            toast.success(`${g.label}: RTP set to ${pct(target)}`);
            await load();
            if (openLog[g.game_id]) loadLog(g.game_id);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to update RTP');
        } finally {
            setSavingId(null);
        }
    };

    const addOverride = async (g) => {
        const form = ovForm[g.game_id] || {};
        const target = parseFloat(form.target);
        if (!form.operator_id) { toast.error('Pick an operator'); return; }
        if (isNaN(target) || target < 30 || target > g.baseline_rtp) {
            toast.error(`Target must be between 30% and the baseline ${g.baseline_rtp}%`);
            return;
        }
        setSavingId(`${g.game_id}-ov`);
        try {
            await axios.put(`${API}/admin/game-rtp/${g.game_id}`, { target_rtp: target, operator_id: form.operator_id }, { withCredentials: true });
            toast.success('Operator RTP override saved');
            setOvForm(prev => ({ ...prev, [g.game_id]: { operator_id: '', target: '' } }));
            await load();
            if (openLog[g.game_id]) loadLog(g.game_id);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to save override');
        } finally {
            setSavingId(null);
        }
    };

    const removeOverride = async (g, operatorId) => {
        setSavingId(`${g.game_id}-rm-${operatorId}`);
        try {
            await axios.delete(`${API}/admin/game-rtp/${g.game_id}/operator/${operatorId}`, { withCredentials: true });
            toast.success('Override removed — reverts to global');
            await load();
            if (openLog[g.game_id]) loadLog(g.game_id);
        } catch (e) {
            toast.error('Failed to remove override');
        } finally {
            setSavingId(null);
        }
    };

    const toggleLog = (gameId) => {
        const next = !openLog[gameId];
        setOpenLog(prev => ({ ...prev, [gameId]: next }));
        if (next && !logs[gameId]) loadLog(gameId);
    };

    const setBonusFrequency = async (levelId) => {
        setSavingFreq(true);
        try {
            const { data } = await axios.put(`${API}/admin/lucky-ducky/bonus-frequency`, { level: levelId }, { withCredentials: true });
            toast.success(`Bonus frequency set to "${levelId}" — fires ~1 in ${data.trigger_one_in} spins`);
            await Promise.all([load(), loadBonusFreq(), loadLive('lucky-ducky')]);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to change bonus frequency');
        } finally {
            setSavingFreq(false);
        }
    };

    return (
        <div className="space-y-6 text-white" data-testid="game-rtp-tab">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h2 className="text-xl font-black flex items-center gap-2">
                        <Percent className="w-5 h-5 text-[#FFD700]" /> Game RTP &amp; House Edge
                    </h2>
                    <p className="text-sm text-slate-400 mt-1 max-w-2xl">
                        Set each game's Return-To-Player target globally or per operator/location. Lower RTP means a
                        bigger house edge and more profit. Targets apply uniformly across every bet level and cannot
                        exceed the game's natural baseline RTP.
                    </p>
                </div>
                <button onClick={load} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#1a1a1a] border border-[#333] hover:border-[#FFD700] text-sm font-bold" data-testid="game-rtp-refresh">
                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
                </button>
            </div>

            <div className="grid gap-4">
                {games.map(g => {
                    const draft = drafts[g.game_id] ?? '';
                    const dirty = String(draft) !== String(g.target_rtp);
                    const lv = live[g.game_id];
                    const form = ovForm[g.game_id] || { operator_id: '', target: '' };
                    const usedOps = new Set((g.operator_overrides || []).map(o => o.operator_id));
                    const availOps = operators.filter(o => !usedOps.has(o.id));
                    return (
                        <div key={g.game_id} className="bg-[#121212] border border-[#333] rounded-xl p-5" data-testid={`game-rtp-card-${g.game_id}`}>
                            {/* Header */}
                            <div className="flex items-center justify-between flex-wrap gap-4">
                                <div>
                                    <div className="text-lg font-black text-[#FFD700]">{g.label}</div>
                                    <div className="text-xs text-slate-500 mt-0.5">
                                        Baseline RTP <span className="text-slate-300 font-bold">{pct(g.baseline_rtp)}</span>
                                        <span className="mx-2">·</span>
                                        Payout factor <span className="text-slate-300 font-bold">{Number(g.house_factor).toFixed(4)}×</span>
                                    </div>
                                </div>
                                <div className="flex items-end gap-4">
                                    <div className="text-center">
                                        <div className="text-[10px] uppercase tracking-wider text-slate-500">Effective RTP</div>
                                        <div className="text-2xl font-black text-[#37d67a]" data-testid={`game-rtp-effective-${g.game_id}`}>{pct(g.effective_rtp)}</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-[10px] uppercase tracking-wider text-slate-500">House Edge</div>
                                        <div className="text-2xl font-black text-[#FF3B30]" data-testid={`game-rtp-edge-${g.game_id}`}>{pct(g.house_edge_pct)}</div>
                                    </div>
                                </div>
                            </div>

                            {/* Live RTP readout */}
                            <div className="mt-4 flex items-center gap-3 flex-wrap bg-[#0d0d0d] border border-[#262626] rounded-lg px-4 py-3" data-testid={`game-rtp-live-${g.game_id}`}>
                                <Activity className="w-4 h-4 text-[#4bd0ff]" />
                                <span className="text-sm text-slate-400">Actual RTP (last {lv ? lv.spins : '—'} real spins):</span>
                                <span className="text-lg font-black text-[#4bd0ff]" data-testid={`game-rtp-live-value-${g.game_id}`}>
                                    {lv ? pct(lv.actual_rtp) : '…'}
                                </span>
                                {lv && lv.spins === 0 && <span className="text-xs text-slate-600">(no real spins yet)</span>}
                                <button onClick={() => loadLive(g.game_id)} className="ml-auto text-xs text-slate-400 hover:text-[#FFD700] flex items-center gap-1" data-testid={`game-rtp-live-refresh-${g.game_id}`}>
                                    <RefreshCw className="w-3 h-3" /> Recalc
                                </button>
                            </div>

                            {/* Bonus-frequency dial (Lucky Ducky) */}
                            {g.game_id === 'lucky-ducky' && bonusFreq && (
                                <div className="mt-4" data-testid="bonus-frequency-dial">
                                    <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2 font-bold flex items-center gap-1.5">
                                        <Flame className="w-3.5 h-3.5 text-[#ff8a2b]" /> Bonus frequency
                                    </div>
                                    <div className="grid grid-cols-3 gap-2">
                                        {(bonusFreq.levels || []).map(lv => {
                                            const active = bonusFreq.current_level === lv.id;
                                            return (
                                                <button
                                                    key={lv.id}
                                                    disabled={savingFreq}
                                                    onClick={() => !active && setBonusFrequency(lv.id)}
                                                    className={`text-left rounded-lg px-3 py-2.5 border transition disabled:opacity-60 ${active ? 'bg-[#ff8a2b]/15 border-[#ff8a2b] shadow-[0_0_16px_rgba(255,138,43,0.35)]' : 'bg-[#0d0d0d] border-[#333] hover:border-[#ff8a2b]/70'}`}
                                                    data-testid={`bonus-freq-${lv.id}`}
                                                >
                                                    <div className={`text-sm font-black ${active ? 'text-[#ffb46b]' : 'text-slate-200'}`}>
                                                        {lv.label}{active && ' ✓'}
                                                    </div>
                                                    <div className="text-[10px] text-slate-400 mt-0.5">~1 in {lv.trigger_one_in} spins</div>
                                                    <div className="text-[10px] text-slate-600 mt-0.5">natural RTP {lv.baseline_rtp}%</div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                    <div className="text-[11px] text-slate-500 mt-2">
                                        Changing frequency keeps your target RTP ({pct(g.target_rtp)}) — the house factor auto-adjusts.
                                    </div>
                                </div>
                            )}

                            {/* Global target editor */}
                            <div className="mt-5">
                                <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2 font-bold">Global target (all operators)</div>
                                <div className="flex items-end gap-3 flex-wrap">
                                    <div>
                                        <input
                                            type="number" step="0.5" min="30" max={g.baseline_rtp} value={draft}
                                            onChange={(e) => setDrafts(prev => ({ ...prev, [g.game_id]: e.target.value }))}
                                            className="w-32 bg-[#0d0d0d] border border-[#333] focus:border-[#FFD700] outline-none rounded-lg px-3 py-2 text-lg font-black text-white"
                                            data-testid={`game-rtp-input-${g.game_id}`}
                                        />
                                        <span className="ml-2 text-slate-500 text-sm">% RTP</span>
                                    </div>
                                    {dirty && !isNaN(parseFloat(draft)) && (
                                        <div className="text-xs text-slate-400 pb-2">→ house edge <span className="text-[#FF3B30] font-bold">{(100 - parseFloat(draft)).toFixed(2)}%</span></div>
                                    )}
                                    <button onClick={() => saveGlobal(g)} disabled={!dirty || savingId === `${g.game_id}-global`}
                                        className="flex items-center gap-2 px-5 py-2 rounded-lg bg-[#FFD700] text-black font-black disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-95"
                                        data-testid={`game-rtp-save-${g.game_id}`}>
                                        <Save className="w-4 h-4" /> {savingId === `${g.game_id}-global` ? 'Saving…' : 'Save'}
                                    </button>
                                </div>
                            </div>

                            {/* Per-operator overrides */}
                            <div className="mt-6 border-t border-[#262626] pt-4">
                                <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2 font-bold">Per-operator overrides</div>
                                {(g.operator_overrides || []).length === 0 && (
                                    <div className="text-xs text-slate-600 mb-3">No overrides — every operator uses the global {pct(g.target_rtp)}.</div>
                                )}
                                <div className="space-y-2 mb-3">
                                    {(g.operator_overrides || []).map(o => (
                                        <div key={o.operator_id} className="flex items-center gap-3 flex-wrap bg-[#0d0d0d] border border-[#262626] rounded-lg px-3 py-2" data-testid={`game-rtp-override-${g.game_id}-${o.operator_id}`}>
                                            <span className="font-bold text-slate-200">{o.operator_label}</span>
                                            <span className="text-sm text-[#37d67a] font-black">{pct(o.effective_rtp)} RTP</span>
                                            <span className="text-xs text-[#FF3B30]">edge {pct(o.house_edge_pct)}</span>
                                            <span className="text-[11px] text-slate-600 ml-auto">{when(o.updated_at)}</span>
                                            <button onClick={() => removeOverride(g, o.operator_id)} disabled={savingId === `${g.game_id}-rm-${o.operator_id}`}
                                                className="text-slate-500 hover:text-[#FF3B30] p-1" title="Remove override"
                                                data-testid={`game-rtp-override-remove-${g.game_id}-${o.operator_id}`}>
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                                <div className="flex items-end gap-2 flex-wrap">
                                    <select value={form.operator_id}
                                        onChange={(e) => setOvForm(prev => ({ ...prev, [g.game_id]: { ...form, operator_id: e.target.value } }))}
                                        className="bg-[#0d0d0d] border border-[#333] focus:border-[#FFD700] outline-none rounded-lg px-3 py-2 text-sm text-white min-w-[180px]"
                                        data-testid={`game-rtp-override-operator-${g.game_id}`}>
                                        <option value="">Select operator…</option>
                                        {availOps.map(o => <option key={o.id} value={o.id}>{o.name || o.email}</option>)}
                                    </select>
                                    <input type="number" step="0.5" min="30" max={g.baseline_rtp} placeholder="RTP %" value={form.target || ''}
                                        onChange={(e) => setOvForm(prev => ({ ...prev, [g.game_id]: { ...form, target: e.target.value } }))}
                                        className="w-24 bg-[#0d0d0d] border border-[#333] focus:border-[#FFD700] outline-none rounded-lg px-3 py-2 text-sm font-bold text-white"
                                        data-testid={`game-rtp-override-target-${g.game_id}`} />
                                    <button onClick={() => addOverride(g)} disabled={savingId === `${g.game_id}-ov`}
                                        className="flex items-center gap-1 px-4 py-2 rounded-lg bg-[#1a1a1a] border border-[#FFD700] text-[#FFD700] font-bold text-sm disabled:opacity-40"
                                        data-testid={`game-rtp-override-add-${g.game_id}`}>
                                        <Plus className="w-4 h-4" /> Add override
                                    </button>
                                </div>
                            </div>

                            {/* Change log */}
                            <div className="mt-5 border-t border-[#262626] pt-3">
                                <button onClick={() => toggleLog(g.game_id)} className="flex items-center gap-2 text-sm font-bold text-slate-300 hover:text-[#FFD700]" data-testid={`game-rtp-log-toggle-${g.game_id}`}>
                                    <History className="w-4 h-4" /> Change log {openLog[g.game_id] ? '▲' : '▼'}
                                </button>
                                {openLog[g.game_id] && (
                                    <div className="mt-3 space-y-1.5" data-testid={`game-rtp-log-${g.game_id}`}>
                                        {(logs[g.game_id] || []).length === 0 && <div className="text-xs text-slate-600">No changes recorded yet.</div>}
                                        {(logs[g.game_id] || []).map((r, i) => (
                                            <div key={i} className="text-xs text-slate-400 flex flex-wrap gap-x-2 items-center border-b border-[#1a1a1a] pb-1">
                                                <span className="text-slate-500">{when(r.changed_at)}</span>
                                                <span className="text-slate-300 font-bold">{r.changed_by}</span>
                                                <span className="text-[#FFD700]">
                                                    {r.action === 'set_global' && 'set global'}
                                                    {r.action === 'set_override' && `override → ${r.operator_label || r.operator_id}`}
                                                    {r.action === 'remove_override' && `removed override → ${r.operator_label || r.operator_id}`}
                                                </span>
                                                <span>
                                                    {r.old_target_rtp != null ? `${r.old_target_rtp}%` : '—'} → {r.new_target_rtp != null ? `${r.new_target_rtp}%` : 'global'}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
                {!loading && games.length === 0 && (
                    <div className="text-slate-500 text-sm">No server-authoritative games registered.</div>
                )}
            </div>
        </div>
    );
}
